import { useState } from 'react';
import { graphql, useMutation } from 'react-relay';

import { useAmpli } from '@/ampli';
import useHumanVerification from '@/hooks/use-human-verification';
import { relayMutationToPromise } from '@/utils/relay';
import { isHumanVerificationError } from '@/utils/waitlist';

import type { useDirectoryAgentClaimRequest_Mutation } from './__generated__/useDirectoryAgentClaimRequest_Mutation.graphql';

type ClaimRequestError = 'AGENT_NOT_FOUND' | 'VERIFICATION_FAILED' | 'UNKNOWN';

const useDirectoryAgentClaimRequest = ({
  agentId,
  agentSlug,
}: {
  agentId: string;
  agentSlug: string;
}) => {
  const ampli = useAmpli();
  const humanVerification = useHumanVerification('landlord_directory_claim');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [commit, isCommitting] =
    useMutation<useDirectoryAgentClaimRequest_Mutation>(graphql`
      mutation useDirectoryAgentClaimRequest_Mutation(
        $input: LandlordRequestDirectoryAgentClaimInput!
        $turnstileToken: String!
      ) {
        landlordRequestDirectoryAgentClaim(
          input: $input
          turnstileToken: $turnstileToken
        ) {
          claimRequestId
          failures
        }
      }
    `);

  const requestClaim = async (
    email: string,
  ): Promise<ClaimRequestError | null> => {
    try {
      const turnstileToken = await humanVerification.getToken();
      const { response, errors } = await relayMutationToPromise(commit, {
        variables: {
          input: { agentId, email },
          turnstileToken,
        },
      });

      if (errors.some(isHumanVerificationError)) {
        return 'VERIFICATION_FAILED';
      }

      const result = response.landlordRequestDirectoryAgentClaim;
      if (!result?.claimRequestId) {
        return result?.failures?.[0] === 'AGENT_NOT_FOUND'
          ? 'AGENT_NOT_FOUND'
          : 'UNKNOWN';
      }

      setIsSubmitted(true);
      ampli((client) =>
        client.directoryAgentClaimSubmitted({ agent_slug: agentSlug }),
      );
      return null;
    } catch (error) {
      console.error('Failed to request a directory agent claim', error);

      return error instanceof Error && isHumanVerificationError(error)
        ? 'VERIFICATION_FAILED'
        : 'UNKNOWN';
    } finally {
      humanVerification.reset();
    }
  };

  return {
    isRequesting: isCommitting || humanVerification.isVerifying,
    isSubmitted,
    requestClaim,
    verificationWidget: humanVerification.widget,
  };
};

export default useDirectoryAgentClaimRequest;
